import { alpha } from "@mui/material/styles";

export const CHECKLIST_ACCENT = "#1a365d";
export const CHECKLIST_ACCENT_LIGHT = "#3182ce";

export const checklistPageSx = {
  width: "210mm",
  minHeight: "297mm",
  boxSizing: "border-box",
  px: "18mm",
  pt: "16mm",
  pb: "20mm",
  bgcolor: "#ffffff",
  margin: { xs: "8px auto", md: "10mm auto" },
  boxShadow: "0 12px 40px rgba(26, 54, 93, 0.1)",
  borderRadius: { xs: 2, md: 0 },
  border: "1px solid",
  borderColor: alpha("#1a365d", 0.08),
  position: "relative",
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  color: "#2d3748",
  fontSize: "10.5pt",
  lineHeight: 1.55,
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${CHECKLIST_ACCENT} 0%, ${CHECKLIST_ACCENT_LIGHT} 55%, #63b3ed 100%)`,
  },
};

export const checklistRootSx = {
  bgcolor: "#eef2f7",
  py: { xs: 1.5, md: 2 },
  px: { xs: 0.5, md: 0 },
};

export const docHeaderShellSx = {
  borderRadius: 2.5,
  p: { xs: 1.5, md: 2 },
  mb: 2.5,
  background: `linear-gradient(135deg, ${alpha("#1a365d", 0.09)} 0%, ${alpha(
    "#3182ce",
    0.06,
  )} 48%, ${alpha("#ffffff", 0.95)} 100%)`,
  border: `1px solid ${alpha("#1a365d", 0.12)}`,
  boxShadow: `0 8px 24px ${alpha("#1a365d", 0.08)}`,
};

export const headerKickerSx = {
  fontSize: "0.68rem",
  color: alpha("#1a365d", 0.75),
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 1.2,
  m: 0,
};

export const headerTitleSx = {
  fontSize: { xs: "1.05rem", md: "1.2rem" },
  color: "#1a202c",
  fontWeight: 900,
  mt: 0.35,
  mb: 1,
  lineHeight: 1.2,
};

export const headerPhasesRowSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 0.6,
};

export const sectionTitleRowSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  mt: 2.5,
  mb: 1.25,
};

export const sectionIndexChipBase = {
  height: 26,
  minWidth: 26,
  fontWeight: 900,
  fontSize: "0.72rem",
  borderRadius: "8px",
  flexShrink: 0,
  "& .MuiChip-label": { px: 0.75 },
};

export const sectionTitleTextSx = {
  fontSize: "0.82rem",
  color: "#1a365d",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  lineHeight: 1.35,
  flex: 1,
  pt: 0.25,
};

export const SECTION_CHIP_COLORS = {
  s1: { bg: alpha("#3949ab", 0.14), color: "#283593" },
  s2: { bg: alpha("#00838f", 0.14), color: "#006064" },
  s3: { bg: alpha("#ef6c00", 0.14), color: "#e65100" },
  s4: { bg: alpha("#7b1fa2", 0.14), color: "#6a1b9a" },
  s5: { bg: alpha("#c2185b", 0.14), color: "#ad1457" },
  default: { bg: alpha("#1a365d", 0.12), color: "#1a365d" },
};

export const subsectionTitleSx = {
  fontSize: "0.78rem",
  color: "#2c5282",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  pl: 0.5,
  mt: 1.75,
  mb: 0.85,
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  "&::before": {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    bgcolor: "#3182ce",
    flexShrink: 0,
  },
};

export const objectiveSx = {
  bgcolor: alpha("#edf2f7", 0.85),
  border: `1px solid ${alpha("#cbd5e0", 0.9)}`,
  borderLeft: `4px solid ${CHECKLIST_ACCENT_LIGHT}`,
  borderRadius: 2,
  px: 1.5,
  py: 1.25,
  fontSize: "10pt",
  color: "#4a5568",
  mb: 1.5,
  "& strong": {
    color: "#1a365d",
    fontWeight: 800,
    mr: 0.5,
  },
};

export const timeLimitShellSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 0.75,
  mb: 1.5,
  p: 1.25,
  borderRadius: 2,
  bgcolor: alpha("#fff5f5", 0.9),
  border: `1px solid ${alpha("#fc8181", 0.35)}`,
};

export const timeLimitLabelSx = {
  fontWeight: 800,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "#9b2c2c",
};

export const boldLabelSx = {
  fontWeight: 800,
  mb: 0.75,
  mt: 1.25,
  fontSize: "0.78rem",
  textTransform: "uppercase",
  letterSpacing: 0.35,
  color: "#2d3748",
};

export const footerSx = {
  mt: "auto",
  pt: 1.25,
  borderTop: `1px solid ${alpha("#e2e8f0", 0.95)}`,
  fontSize: "8.5pt",
  color: "#718096",
  display: "flex",
  justifyContent: "space-between",
  fontWeight: 600,
};

export const panelGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gap: 2,
  mt: 2,
};

export const acceptablePanelSx = {
  border: `1px solid ${alpha("#48bb78", 0.35)}`,
  borderRadius: 2.5,
  p: 1.75,
  bgcolor: alpha("#f0fff4", 0.95),
  boxShadow: `0 6px 18px ${alpha("#48bb78", 0.08)}`,
};

export const rejectPanelSx = {
  border: `1px solid ${alpha("#f56565", 0.35)}`,
  borderRadius: 2.5,
  p: 1.75,
  bgcolor: alpha("#fff5f5", 0.95),
  boxShadow: `0 6px 18px ${alpha("#fc8181", 0.08)}`,
};

export const panelTitleSx = (color) => ({
  fontWeight: 800,
  fontSize: "0.72rem",
  color,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  mb: 1,
  display: "flex",
  alignItems: "center",
  gap: 0.75,
});

export const metaFinalSx = {
  bgcolor: alpha("#ebf8ff", 0.95),
  border: `1px solid ${alpha("#63b3ed", 0.45)}`,
  borderRadius: 2.5,
  p: 1.75,
  mt: 2.5,
  fontSize: "10pt",
  color: "#2b6cb0",
  textAlign: "justify",
  lineHeight: 1.55,
  "& strong": { fontWeight: 800 },
};

export const qualityGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 1.5,
  p: 1.25,
  borderRadius: 2,
  bgcolor: alpha("#f7fafc", 0.8),
  border: `1px solid ${alpha("#e2e8f0", 0.9)}`,
};

export const checklistUlSx = {
  listStyle: "none",
  p: 0,
  m: 0,
  mb: 1.5,
  "& > li": {
    position: "relative",
    pl: 3.25,
    mb: 1,
    py: 0.35,
    borderRadius: 1.5,
    transition: "background-color 0.15s ease",
    "&:hover": {
      bgcolor: alpha("#edf2f7", 0.55),
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "0.55em",
      width: 14,
      height: 14,
      borderRadius: "4px",
      border: `2px solid ${alpha("#3182ce", 0.45)}`,
      bgcolor: "#fff",
      boxSizing: "border-box",
    },
  },
  "& .nested": {
    listStyle: "none",
    pl: "4mm",
    mt: 0.75,
    mb: 0,
    "& li": {
      pl: 2.75,
      "&::before": {
        width: 10,
        height: 10,
        borderRadius: "50%",
        border: `2px solid ${alpha("#718096", 0.4)}`,
      },
    },
  },
};
